"use client";

import type { DetectedWallet, Eip1193Provider, SolanaProvider } from "./types";
import { WalletError } from "./types";

type Eip6963Announcement = {
  info: { uuid: string; name: string; icon: string; rdns: string };
  provider: Eip1193Provider;
};

type InjectedWindow = Window & {
  ethereum?: Eip1193Provider & { isMetaMask?: boolean };
  solana?: SolanaProvider & { isPhantom?: boolean };
  solflare?: SolanaProvider & { isSolflare?: boolean };
  backpack?: SolanaProvider;
  phantom?: { solana?: SolanaProvider };
};

const EVM_PROVIDERS = new Map<string, Eip1193Provider>();
const SOLANA_PROVIDERS = new Map<string, SolanaProvider>();

const injected = () => window as InjectedWindow;

const SOLANA_CANDIDATES: { id: string; name: string; at: (win: InjectedWindow) => SolanaProvider | undefined }[] = [
  { id: "solana:phantom", name: "Phantom", at: (win) => win.phantom?.solana },
  { id: "solana:solflare", name: "Solflare", at: (win) => win.solflare },
  { id: "solana:backpack", name: "Backpack", at: (win) => win.backpack },
  { id: "solana:injected", name: "Solana Wallet", at: (win) => win.solana },
];

const solanaWallets = () => {
  const win = injected();
  const found: DetectedWallet[] = [];
  const seen = new Set<SolanaProvider>();

  for (const candidate of SOLANA_CANDIDATES) {
    const provider = candidate.at(win);

    if (!provider || typeof provider.signMessage !== "function" || seen.has(provider)) {
      continue;
    }

    seen.add(provider);
    SOLANA_PROVIDERS.set(candidate.id, provider);
    found.push({ id: candidate.id, name: candidate.name, icon: null, chain: "solana" });
  }

  return found;
};

export const discoverWallets = (onFound: (wallets: DetectedWallet[]) => void) => {
  const found = new Map<string, DetectedWallet>();

  const publish = () => onFound([...found.values()]);

  const onAnnounce = (event: Event) => {
    const { info, provider } = (event as CustomEvent<Eip6963Announcement>).detail;

    EVM_PROVIDERS.set(info.rdns, provider);
    found.set(info.rdns, { id: info.rdns, name: info.name, icon: info.icon, chain: "evm" });
    publish();
  };

  window.addEventListener("eip6963:announceProvider", onAnnounce);
  window.dispatchEvent(new Event("eip6963:requestProvider"));

  const legacy = injected().ethereum;

  if (found.size === 0 && legacy) {
    EVM_PROVIDERS.set("evm:injected", legacy);
    found.set("evm:injected", {
      id: "evm:injected",
      name: legacy.isMetaMask ? "MetaMask" : "Browser Wallet",
      icon: null,
      chain: "evm",
    });
  }

  for (const wallet of solanaWallets()) {
    found.set(wallet.id, wallet);
  }

  publish();

  return () => window.removeEventListener("eip6963:announceProvider", onAnnounce);
};

export const evmProvider = (id: string) => {
  const provider = EVM_PROVIDERS.get(id);

  if (!provider) {
    throw new WalletError("missing", "That wallet is no longer available in this browser.");
  }

  return provider;
};

export const solanaProvider = (id: string) => {
  const provider = SOLANA_PROVIDERS.get(id);

  if (!provider) {
    throw new WalletError("missing", "That wallet is no longer available in this browser.");
  }

  return provider;
};
